import clsx from 'clsx';
import { useRef, useState } from 'react';
import { AvatarWithFavorite, Badge, Icon, Icons, Typography, TypographyType } from '../../../../components';
import {
  ITEM_ACTIVE_BG_COLOR,
  ITEM_HOVER_BG_COLOR,
  PROSPECT_CATEGORY_TO_COLOR,
  PROSPECT_TYPE_TO_LABEL,
} from '../../../../constants';
import { useDetectTextOverflow } from '../../../../hooks';
import { ComponentSize, PracticeProspect, ProspectCategory, ProspectStatus, TextColor } from '../../../../types';
import { getTooltipWithPlacement, isProspectLocked, snakeCaseToLabel } from '../../../../utils';
import { SimActionType } from '../../SimActionModals';
import ExplorerSimulationActions from './ExplorerSimulationActions';

const MAX_LINES = 1;

interface ExplorerSimulationItemProps {
  active: boolean;
  canManageSims: boolean;
  prospect: PracticeProspect;
  openSimModal: (type: SimActionType) => void;
  toggleOpenSimulationDetails: () => void;
}

/**
 * Renders a single simulation item in the explorer.
 * Displays simulation metadata and handles selection.
 */
const ExplorerSimulationItem = ({
  active,
  canManageSims,
  prospect,
  openSimModal,
  toggleOpenSimulationDetails,
}: ExplorerSimulationItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { firstName, lastName, accountName, jobTitle, category, isFavorited, isUsed, personaId, status, type } =
    prospect;

  const isArchived = status === ProspectStatus.ARCHIVED;
  const isQuiz = category === ProspectCategory.QUIZ;

  // TODO: Remove prospect.isLocked check once FYP is released. @yomnashaban
  const canToggle = !isQuiz || canManageSims || !prospect.isLocked;
  const isLocked = isQuiz && !canManageSims && isProspectLocked(prospect);

  const fullName = `${firstName} ${lastName}`;
  const fullNameRef = useRef<HTMLDivElement>(null);
  const isFullNameOverflowing = useDetectTextOverflow(fullNameRef, MAX_LINES);

  const accountAndJobTitle = `${accountName} | ${jobTitle}`;
  const accountAndJobTitleRef = useRef<HTMLDivElement>(null);
  const isAccountAndJobTitleOverflowing = useDetectTextOverflow(accountAndJobTitleRef, MAX_LINES);

  const getBgClasses = () => {
    let classes = '';
    if (active) {
      // Simulation is open.
      classes += ITEM_ACTIVE_BG_COLOR;
    } else {
      // Simulation is closed.
      // Enable hover effects if the simulation can be toggled.
      if (canToggle) classes += ' ' + ITEM_HOVER_BG_COLOR;
      // Apply disabled styles if the simulation is archived or locked.
      if (isArchived || isLocked) classes += ' opacity-40';
    }
    return classes;
  };

  return (
    <div
      className={clsx('relative flex w-full items-center gap-2 p-2', canToggle && 'cursor-pointer', getBgClasses())}
      onClick={canToggle ? toggleOpenSimulationDetails : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(isArchived || isLocked) && (
        <div className="bg-base-200 flex h-6 w-6 flex-none items-center justify-center rounded-full">
          <Icons icon={isArchived ? Icon.ARCHIVE : Icon.LOCK} />
        </div>
      )}
      {!isArchived && !isLocked && (
        <AvatarWithFavorite isFavorited={isFavorited} label={firstName} size={ComponentSize.X_SMALL} />
      )}
      <div className="flex w-full flex-col gap-2">
        <div>
          <Typography
            type={TypographyType.H4}
            ref={fullNameRef}
            maxLines={MAX_LINES}
            tooltip={isFullNameOverflowing ? getTooltipWithPlacement(fullName) : undefined}
            className="truncate"
          >
            {fullName}
          </Typography>
          <Typography
            type={TypographyType.X_SMALL}
            color={TextColor.SECONDARY}
            ref={accountAndJobTitleRef}
            maxLines={MAX_LINES}
            tooltip={isAccountAndJobTitleOverflowing ? getTooltipWithPlacement(accountAndJobTitle) : undefined}
            className="truncate break-all"
          >
            {accountAndJobTitle}
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={PROSPECT_CATEGORY_TO_COLOR[category]} label={snakeCaseToLabel(category)} />
          <Typography
            color={active ? TextColor.SECONDARY : TextColor.TERTIARY}
            maxLines={MAX_LINES}
            type={TypographyType.X_SMALL}
          >
            {PROSPECT_TYPE_TO_LABEL[type]}
          </Typography>
        </div>
      </div>
      {isHovered && (
        <ExplorerSimulationActions
          isDraft={prospect.isDraft}
          isFavorited={isFavorited}
          isUsed={isUsed}
          personaId={personaId}
          prospectCategory={category}
          prospectUserId={prospect.userId}
          status={status}
          onClose={() => setIsHovered(false)}
          openSimModal={openSimModal}
        />
      )}
    </div>
  );
};

export default ExplorerSimulationItem;
